<template>
  <section class="bg-[radial-gradient(circle_at_top_left,#fff9f1_0%,#f8f1ea_36%,#f3ebe3_100%)] py-5 sm:py-6 lg:py-8">
    <div class="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
      <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white/90 p-8 text-sm text-muted shadow-sm">
        Loading venue preview...
      </div>

      <div v-else class="space-y-6 lg:space-y-8">
        <nav class="flex flex-wrap items-center gap-2 text-sm text-[#7d6653]">
          <button type="button" class="inline-flex items-center gap-2 rounded-full border border-[#e4d6ca] bg-white px-4 py-2 font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand" @click="goBack">
            <i class="ri-arrow-left-line"></i>
            Back
          </button>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">Business</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">{{ businessName }}</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-semibold text-ink">{{ venue.title || 'Venue detail' }}</span>
        </nav>

        <div v-if="error" class="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-800">
          {{ error }}
        </div>

        <section class="overflow-hidden rounded-[34px] border border-[#eadfd4] bg-[linear-gradient(180deg,#fffaf5_0%,#fffdfb_100%)] shadow-[0_18px_55px_rgba(79,55,36,0.08)]">
          <div class="grid xl:grid-cols-[minmax(0,1.12fr)_420px]">
            <div class="p-5 sm:p-7 lg:p-8">
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div class="max-w-3xl">
                  <p class="text-xs font-semibold uppercase tracking-[0.28em] text-[#b78969]">Venue Detail Preview</p>
                  <h1 class="mt-3 font-serif text-[2.15rem] font-semibold leading-[1.05] text-ink sm:text-[2.65rem] lg:text-[3.2rem]">
                    {{ venue.title || 'Venue detail' }}
                  </h1>
                  <p class="mt-3 max-w-2xl font-serif text-lg italic text-[#927660] sm:text-[1.35rem]">
                    {{ venue.tagLine || 'Where moments become memories' }}
                  </p>
                </div>

                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="item in previewBadges"
                    :key="item.label"
                    class="rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm"
                    :class="item.tone"
                  >
                    {{ item.label }}
                  </span>
                </div>
              </div>

              <div class="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <article v-for="fact in heroFacts" :key="fact.label" class="rounded-[22px] border border-[#eadfd4] bg-white/90 px-4 py-4 shadow-sm">
                  <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#b78969]">{{ fact.label }}</p>
                  <p class="mt-2 text-sm font-semibold text-ink sm:text-[0.98rem]">{{ fact.value }}</p>
                </article>
              </div>

              <div class="mt-7 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <p class="text-[1.02rem] leading-8 text-[#2f241b] sm:text-[1.08rem]">
                    {{ venueStory }}
                  </p>

                  <div class="mt-5 rounded-[20px] border border-[#ead8b8] bg-[#fff8e6] px-4 py-4 text-[0.98rem] italic leading-7 text-[#6f532c] sm:px-5">
                    <span class="font-semibold">💡 {{ venueCallout }}</span>
                  </div>
                </div>

                <div class="rounded-[24px] border border-[#eadfd4] bg-[#f8f2eb] p-5">
                  <p class="text-xs font-semibold uppercase tracking-[0.2em] text-[#b78969]">Quick profile</p>
                  <div class="mt-4 space-y-3 text-sm text-[#4b3b2f]">
                    <div class="flex items-center justify-between gap-3">
                      <span>Business</span>
                      <span class="text-right font-semibold text-ink">{{ businessName }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span>Venue type</span>
                      <span class="text-right font-semibold text-ink">{{ venue.type || 'Not set' }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span>Status</span>
                      <span class="text-right font-semibold text-ink">{{ venue.status || 'Draft' }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span>Established</span>
                      <span class="text-right font-semibold text-ink">{{ establishedLabel }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-7 grid gap-5 lg:grid-cols-2">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue highlights</p>
                  <div class="mt-3 flex flex-wrap gap-2">
                    <span v-for="item in venueHighlightsList" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                      {{ item }}
                    </span>
                  </div>
                </div>

                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">In-house services</p>
                  <div class="mt-3 grid gap-3 sm:grid-cols-2">
                    <div v-for="column in inHouseServiceColumns" :key="column.join('-')" class="space-y-2">
                      <div v-for="item in column" :key="item" class="flex items-center gap-3 text-sm text-[#2f241b] sm:text-[0.98rem]">
                        <span class="text-[#2e8a5d]">✓</span>
                        <span>{{ item }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-t border-[#eadfd4] bg-[#f4e9dd] p-4 sm:p-5 xl:border-l xl:border-t-0">
              <div class="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#9f7a5d]">Gallery preview</p>
                  <p class="mt-1 text-sm text-[#6f5948]">Tap any image to open the full gallery</p>
                </div>
                <button type="button" class="rounded-full border border-[#d8c7b8] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#6f5948] transition hover:border-brand hover:text-brand" @click="openVenueGallery()">
                  View all
                </button>
              </div>

              <div class="grid grid-cols-2 gap-3 auto-rows-[108px] sm:auto-rows-[132px]">
                <button
                  type="button"
                  class="group relative col-span-2 row-span-2 overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] text-left"
                  @click="openVenueGalleryById(previewGalleryTiles[0])"
                >
                  <img
                    :src="previewGalleryTiles[0].src"
                    :alt="venue.title"
                    class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                  >
                  <div class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1f1208]/70 to-transparent px-4 py-4 text-white">
                    <div class="flex flex-wrap gap-2">
                      <span v-if="previewGalleryTiles[0]?.isCover" class="rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand">
                        Cover image
                      </span>
                      <span v-if="previewGalleryTiles[0]?.tagName" class="rounded-full bg-[#1f1208]/55 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                        {{ previewGalleryTiles[0].tagName }}
                      </span>
                      <span v-if="previewGalleryTiles[0]?.isPlaceholder" class="rounded-full bg-[#1f1208]/55 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                        Preview placeholder
                      </span>
                    </div>
                  </div>
                </button>

                <button
                  v-for="(image, index) in sideImages"
                  :key="image.id || index"
                  type="button"
                  class="group relative overflow-hidden rounded-[18px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] text-left"
                  @click="openVenueGalleryById(image)"
                >
                  <img :src="image.src" :alt="`${venue.title} ${index + 2}`" class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]">
                  <span v-if="image.tagName" class="absolute left-3 top-3 rounded-full bg-[#1f1208]/65 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                    {{ image.tagName }}
                  </span>
                  <span v-if="image.isPlaceholder" class="absolute bottom-3 left-3 rounded-full bg-white/92 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#7b624d] shadow-sm">
                    Placeholder
                  </span>
                  <div
                    v-if="image.overlayText"
                    class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
                  >
                    <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                    <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        <div class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div class="space-y-6">
            <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Preferred partners</p>
                  <h3 class="mt-2 text-lg font-semibold text-ink">{{ partners.length }} linked partners</h3>
                </div>
              </div>

              <div v-if="partners.length" class="mt-4 space-y-3">
                <article v-for="item in partnerPreview" :key="item.id" class="rounded-2xl border border-[#eadfd4] bg-[#fffaf5] px-4 py-3">
                  <div class="flex items-start justify-between gap-3">
                    <div>
                      <p class="text-sm font-semibold text-ink">{{ item.name }}</p>
                      <p class="mt-1 text-xs font-semibold uppercase tracking-wide text-[#b78969]">{{ item.category || 'Partner' }}</p>
                    </div>
                    <span class="text-sm text-muted">{{ item.phone || 'No phone' }}</span>
                  </div>
                </article>
              </div>
              <p v-else class="mt-4 rounded-2xl border border-dashed border-[#eadfd4] bg-[#fffaf5] px-4 py-4 text-sm text-muted">
                No venue partners added yet.
              </p>
            </section>

            <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">About venue</p>
                <h2 class="mt-2 font-serif text-[1.7rem] font-semibold text-ink sm:text-[1.9rem]">Overview</h2>
              </div>

              <div class="mt-5 grid gap-4 md:grid-cols-2">
                <article class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                  <p class="text-xs font-semibold uppercase tracking-wide text-muted">Description</p>
                  <p class="mt-3 text-sm leading-7 text-[#4b3b2f]">{{ venue.about || 'Add a venue description to make this section more descriptive.' }}</p>
                </article>
                <article class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                  <p class="text-xs font-semibold uppercase tracking-wide text-muted">Venue details</p>
                  <div class="mt-3 space-y-3 text-sm text-[#4b3b2f]">
                    <div class="flex items-center justify-between gap-4">
                      <span>Venue type</span>
                      <span class="font-semibold text-ink">{{ venue.type || 'Not set' }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                      <span>Business</span>
                      <span class="font-semibold text-ink">{{ businessName }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                      <span>City</span>
                      <span class="font-semibold text-ink">{{ venueCity }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                      <span>Published status</span>
                      <span class="font-semibold text-ink">{{ venue.status || 'Draft' }}</span>
                    </div>
                  </div>
                </article>
              </div>
            </section>

            <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue configurations</h2>
            </div>

            <div v-if="featuredSpace" class="mt-5">
              <div class="flex flex-wrap gap-3">
                <button
                  v-for="item in spaceTabs"
                  :key="item.id"
                  type="button"
                  class="rounded-full border px-4 py-2 text-sm font-semibold transition"
                  :class="featuredSpace.id === item.id ? 'border-[#cda226] bg-[#fff8e7] text-[#9e7b43]' : 'border-[#e5d8cc] bg-white text-[#6f5948] hover:border-[#cda226]'"
                  @click="selectedSpaceId = item.id"
                >
                  <span class="mr-2">{{ item.icon }}</span>{{ item.name }}
                </button>
              </div>

              <article class="mt-5 overflow-hidden rounded-[24px] border border-[#eadfd4] bg-[#fffdf9]">
                <div class="flex flex-wrap items-start justify-between gap-4 border-b border-[#eadfd4] px-6 py-5">
                  <div>
                    <h3 class="font-serif text-[1.9rem] font-semibold text-ink">{{ featuredSpace.name }}</h3>
                    <div class="mt-3 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span class="rounded-full bg-[#f5eee8] px-3 py-1 font-semibold">{{ featuredSpace.setting }}</span>
                      <span class="rounded-full bg-[#f5eee8] px-3 py-1 font-semibold">{{ featuredSpace.spaceType || 'Hall' }}</span>
                      <span v-if="featuredSpace.areaSqft" class="rounded-full bg-[#f5eee8] px-3 py-1 font-semibold">📐 {{ featuredSpace.areaSqft }} sqft</span>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-[1.9rem] font-semibold text-[#1f1208]">{{ featuredSpace.primaryPrice }}</p>
                    <p class="mt-1 text-sm text-muted">{{ featuredSpace.secondaryPrice }}</p>
                  </div>
                </div>

                <div class="px-6 py-6">
                  <p class="text-[1.02rem] leading-8 text-[#2f241b]">
                    {{ featuredSpace.longDescription }}
                  </p>

                  <div class="mt-6">
                    <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Seating configurations</p>
                    <div class="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <article v-for="seat in featuredSpace.seatingCards" :key="seat.label" class="rounded-[16px] border border-[#eadfd4] bg-white px-4 py-4">
                        <div class="flex items-center justify-between gap-3">
                          <span class="text-sm text-[#2f241b]">{{ seat.label }}</span>
                          <span class="text-sm font-semibold text-[#1f1208]">{{ seat.value }}</span>
                        </div>
                      </article>
                    </div>
                  </div>

                  <div class="mt-8 grid gap-8 md:grid-cols-2">
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Included amenities</p>
                      <div class="mt-4 space-y-2">
                        <div v-for="item in featuredSpace.amenities" :key="item" class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                          <span class="text-[#2e8a5d]">✓</span>
                          <span>{{ item }}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Equipment</p>
                      <div class="mt-4 space-y-2">
                        <div v-for="item in featuredSpace.equipment" :key="item.label" class="flex items-center justify-between gap-4 text-[1rem] text-[#2f241b]">
                          <span class="inline-flex items-center gap-3">
                            <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c69225]'">{{ item.included ? '✓' : '+' }}</span>
                            <span>{{ item.label }}</span>
                          </span>
                          <span class="text-sm font-semibold" :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c69225]'">
                            {{ item.value }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
            <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
              No spaces configured yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue packages</h2>
            </div>

            <div v-if="packages.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article v-for="item in packages" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <div class="mt-2 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.priceLabel }}</span>
                      <span v-if="item.maxGuests" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.maxGuests }} guests</span>
                      <span v-if="item.validHours" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.validHours }} hrs</span>
                    </div>
                  </div>
                </div>

                <p v-if="item.description" class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description }}</p>

                <div v-if="item.includedServices.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="service in item.includedServices.slice(0, 6)" :key="service" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ service }}
                  </span>
                </div>
              </article>
            </div>
            <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
              No packages configured yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Menus & Catering</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Menus & Catering</h2>
              <p class="mt-2 text-[1.02rem] text-[#927660]">{{ menuBenchmarkText }}</p>
            </div>

            <div class="mt-5 flex flex-wrap gap-3">
              <button
                v-for="item in menuTabs"
                :key="item.id"
                type="button"
                class="rounded-full border px-4 py-2 text-sm font-semibold transition"
                :class="selectedMenu?.id === item.id ? item.activeTone : 'border-[#e5d8cc] bg-white text-[#6f5948] hover:border-[#cda226]'"
                @click="selectedMenuId = item.id"
              >
                <span class="mr-2">{{ item.dot }}</span>{{ item.name }}
              </button>
            </div>

            <article v-if="selectedMenu" class="mt-5 overflow-hidden rounded-[24px] border border-[#eadfd4] bg-[#fffdf9]">
              <div class="flex flex-wrap items-start justify-between gap-4 border-b border-[#eadfd4] px-6 py-5">
                <div>
                  <h3 class="font-serif text-[1.9rem] font-semibold text-ink">{{ selectedMenu.name }}</h3>
                  <p class="mt-2 text-sm text-[#927660]">{{ selectedMenu.minimumLabel }}</p>
                </div>
                <div class="text-right">
                  <p class="text-[1.9rem] font-semibold text-[#1f1208]">{{ selectedMenu.price }}</p>
                  <p class="mt-1 text-sm text-muted">per plate</p>
                </div>
              </div>

              <div class="grid gap-8 px-6 py-6 md:grid-cols-2">
                <div v-for="group in selectedMenu.groups" :key="group.title" class="space-y-3">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">{{ group.title }}</p>
                  <div class="space-y-2">
                    <div v-for="item in group.items" :key="item" class="flex items-start gap-3 text-[1rem] text-[#2f241b]">
                      <span class="mt-1 text-[#d8c8bb]">•</span>
                      <span>{{ item }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Policies</h2>
            </div>

            <div class="mt-6 grid gap-8 md:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue rules</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in venueRuleItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Booking terms</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Gallery tags</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Photo library snapshot</h2>
            </div>

            <div v-if="galleryPreviewCards.length" class="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <button
                v-for="item in galleryPreviewCards"
                :key="item.id"
                type="button"
                class="group overflow-hidden rounded-[20px] border border-[#eadfd4] bg-[#fffaf5] text-left transition hover:border-[#d9c2ac]"
                @click="openVenueGalleryById(item)"
              >
                <div class="relative h-28 bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] sm:h-32">
                  <img :src="item.src" :alt="venue.title" class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]">
                  <span v-if="item.isCover" class="absolute left-3 top-3 rounded-full bg-white/92 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                    Cover
                  </span>
                </div>
                <div class="px-3 py-3">
                  <p class="truncate text-[11px] font-semibold uppercase tracking-wide text-[#b78969]">{{ item.tagName || 'Venue photo' }}</p>
                </div>
              </button>
            </div>
            <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
              No gallery images uploaded yet.
            </p>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
            <h2 class="font-serif text-[2rem] font-semibold text-ink">{{ locationHeading }}</h2>

            <div class="relative mt-5 overflow-hidden rounded-[22px] border border-[#d5e4ec] bg-[#dcecf5]">
              <div class="absolute inset-0 bg-[linear-gradient(0deg,rgba(255,255,255,0.18)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.18)_1px,transparent_1px)] bg-[size:112px_64px]"></div>
              <div class="relative flex min-h-[220px] flex-col items-center justify-center px-6 py-10 text-center">
                <div class="flex h-12 w-12 items-center justify-center rounded-full bg-[#ff5b4d] text-xl text-white shadow-[0_10px_20px_rgba(255,91,77,0.3)]">
                  <i class="ri-map-pin-2-fill"></i>
                </div>
                <p class="mt-5 font-serif text-[1.35rem] font-semibold text-ink">{{ locationHeading }}</p>
                <p class="mt-1 text-[1.02rem] text-[#5f7180]">{{ venueCityRegion }}</p>
                <a
                  :href="googleMapsUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="mt-6 inline-flex items-center rounded-full bg-[#cda226] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#b58d1d]"
                >
                  Open in Google Maps ↗
                </a>
              </div>
            </div>
          </section>
        </div>

        <aside class="self-start xl:sticky xl:top-6">
          <section class="overflow-hidden rounded-[28px] border border-[#eadfcf] bg-[#fcf7eb] shadow-[0_14px_40px_rgba(79,55,36,0.08)]">
              <div class="border-b border-[#eadfcf] px-5 py-5">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#9e7b43]">Send inquiry</p>
                <h3 class="mt-3 font-serif text-[1.6rem] font-semibold text-ink">{{ venue.title || 'Venue detail' }}</h3>
                <p class="mt-1 text-sm text-[#7c6550]">
                  {{ venueCity }} · {{ depositLabel }}
                </p>
              </div>

              <div class="px-5 py-5">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#9e7b43]">Choose a package</p>

                <div class="mt-4 space-y-3">
                  <div
                    v-for="item in inquiryPackages"
                    :key="item.id"
                    role="button"
                    tabindex="0"
                    class="relative w-full rounded-[18px] border px-4 py-4 text-left transition"
                    :class="selectedPackage?.id === item.id ? 'border-[#cda226] bg-[#fff8e7] shadow-sm' : 'border-[#e6d9ca] bg-white hover:border-[#d8bb73]'"
                    @click="selectedPackageId = item.id"
                    @keydown.enter.prevent="selectedPackageId = item.id"
                    @keydown.space.prevent="selectedPackageId = item.id"
                  >
                    <span
                      v-if="item.badge"
                      class="absolute -right-1 -top-2 rounded-full bg-[#cda226] px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white"
                    >
                      {{ item.badge }}
                    </span>

                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <p class="text-lg font-semibold text-ink">{{ item.name }}</p>
                        <p class="mt-1 text-sm leading-6 text-[#7c6550]">{{ item.description }}</p>
                      </div>
                      <p class="text-base font-semibold text-[#1f1208]">{{ item.priceLabel }}</p>
                    </div>

                    <p class="mt-2 text-sm text-[#6f5948]">{{ item.metaLine }}</p>

                    <div class="mt-3 flex flex-wrap gap-2">
                      <span v-for="service in item.includedServices.slice(0, 6)" :key="service" class="rounded-full bg-[#f8efd7] px-2.5 py-1 text-[11px] font-medium text-[#9e7b43]">
                        {{ service }}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  class="mt-5 w-full rounded-[16px] bg-[#cda226] px-5 py-4 text-sm font-semibold text-white transition hover:bg-[#b58d1d]"
                >
                  Continue with {{ selectedPackage?.name || 'Package' }} →
                </button>
              </div>
            </section>
        </aside>
      </div>
    </div>
  </div>
</section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import api from '@/services/api'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const businessName = ref(String(route.query.businessName || 'Business'))
const venue = ref({
  id: '',
  title: '',
  type: '',
  tagLine: '',
  about: '',
  address: '',
  status: '',
  yearEstablished: '',
  phone: '',
  phoneCode: '',
  email: '',
  website: '',
  highlights: [],
  inHouseServices: []
})
const spaces = ref([])
const packages = ref([])
const partners = ref([])
const gallery = ref([])
const galleryLightbox = ref(null)
const selectedPackageId = ref('')
const selectedSpaceId = ref('')
const selectedMenuId = ref('')
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})

const previewPlaceholderImages = [
  {
    id: 'placeholder-1',
    src: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1400&q=80',
    tagName: 'Banquet hall',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-2',
    src: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=900&q=80',
    tagName: 'Table setup',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-3',
    src: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=900&q=80',
    tagName: 'Decor',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-4',
    src: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=900&q=80',
    tagName: 'Outdoor portrait',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-5',
    src: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=900&q=80',
    tagName: 'Dining room',
    isCover: false,
    isPlaceholder: true
  }
]

const normalizeApiCollection = (data, keys = []) => {
  const root = data?.data ?? data
  if (Array.isArray(root)) return root
  for (const key of keys) {
    if (Array.isArray(root?.[key])) return root[key]
  }
  return []
}

const splitToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const normalizeVenue = (item = {}) => ({
  id: String(item?.id || item?.venue_id || item?.vendor_business_venue_id || route.params.venueId || ''),
  title: item?.title || item?.name || 'Untitled venue',
  type: item?.type || item?.venue_type || '',
  tagLine: item?.tag_line || item?.tagLine || '',
  about: item?.about || '',
  address: item?.address || item?.location || '',
  status: String(item?.status || '').trim() === '1' ? 'Active' : item?.status || 'Draft',
  yearEstablished: item?.est_year || item?.year_established || item?.yearEstablished || '',
  phone: item?.phone || '',
  phoneCode: item?.phone_code || item?.phoneCode || '',
  email: item?.email || '',
  website: item?.website || '',
  highlights: splitToArray(item?.highlights || item?.venue_highlights),
  inHouseServices: splitToArray(item?.in_house_services || item?.inHouseServices)
})

const normalizeSpace = (item = {}, index = 0) => {
  const configurations = splitToArray(item?.seat_config || item?.seatConfig)
  const capacities = splitToArray(item?.seat_capacity || item?.seatCapacity)
  const amenities = splitToArray(item?.amenities)
  return {
    id: String(item?.space_id || item?.id || `space-${index}`),
    name: item?.name || 'Untitled space',
    spaceType: item?.space_type || item?.spaceType || '',
    setting: item?.setting || 'Indoor',
    areaSqft: item?.area_sqft || item?.areaSqft || '',
    description: item?.description || '',
    minPrice: item?.min_price || item?.minPrice || '',
    maxPrice: item?.max_price || item?.maxPrice || '',
    priceLabel: formatPriceRange(item?.min_price || item?.minPrice, item?.max_price || item?.maxPrice),
    amenities,
    seatingSummary: configurations.map((label, configIndex) => {
      const capacity = capacities[configIndex]
      return capacity ? `${label}: ${capacity}` : label
    })
  }
}

const normalizePackage = (item = {}, index = 0) => ({
  id: String(item?.package_id || item?.id || `package-${index}`),
  name: item?.name || 'Untitled package',
  description: item?.description || '',
  price: item?.price || '',
  maxGuests: item?.max_guests || item?.maxGuests || '',
  validHours: item?.valid_hours || item?.validHours || '',
  includedServices: splitToArray(item?.included_services || item?.includedServices),
  priceLabel: formatCurrency(item?.price) || 'Price on request'
})

const normalizePartner = (item = {}, index = 0) => ({
  id: String(item?.vendor_partner_id || item?.id || `partner-${index}`),
  name: item?.name || 'Partner',
  category: item?.category || '',
  phone: item?.phone || ''
})

const normalizeVenueGalleryEntries = (source) => {
  const items = Array.isArray(source) ? source : [source]
  return items
    .flatMap((item, itemIndex) => {
      if (!item) return []
      if (typeof item === 'string') {
        return [{
          id: `gallery-${itemIndex}-${item}`,
          src: item,
          tagName: '',
          isCover: false
        }]
      }
      const imageKeys = ['image1', 'image2', 'image3', 'image4', 'image', 'image_url', 'url', 'path']
      const entryId = String(item?.id || item?.gallery_id || item?.venue_gallery_id || item?.img_id || '')
      return imageKeys
        .map((key, keyIndex) => {
          const src = item?.[key] || ''
          if (!src) return null
          return {
            id: entryId || `gallery-${itemIndex}-${keyIndex}-${src}`,
            src,
            tagName: String(item?.tag_name || item?.tagName || item?.name || '').trim(),
            isCover: String(item?.is_cover || item?.isCover || '0') === '1'
          }
        })
        .filter(Boolean)
    })
    .filter((entry) => entry?.src)
}

const formatCurrency = (value) => {
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? `Rs. ${numeric.toLocaleString('en-IN')}` : ''
}

const formatPriceRange = (minPrice, maxPrice) => {
  const minValue = formatCurrency(minPrice)
  const maxValue = formatCurrency(maxPrice)
  if (minValue && maxValue) return `${minValue} - ${maxValue}`
  return minValue || maxValue || 'Price on request'
}

const venueCity = computed(() => {
  const address = String(venue.value.address || '').trim()
  if (!address) return 'City'
  const segments = address.split(',').map((item) => item.trim()).filter(Boolean)
  return segments.length >= 2 ? segments[segments.length - 2] : segments[0] || 'City'
})

const locationHeading = computed(() => String(venue.value.address || venue.value.title || 'Venue location').trim() || 'Venue location')
const venueCityRegion = computed(() => {
  const address = String(venue.value.address || '').trim()
  if (!address) return 'Location details not available'
  const segments = address.split(',').map((item) => item.trim()).filter(Boolean)
  return segments.slice(-2).join(', ') || address
})
const googleMapsUrl = computed(() => {
  const query = String(venue.value.address || venue.value.title || '').trim()
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || 'venue location')}`
})

const establishedLabel = computed(() => venue.value.yearEstablished ? `Est. ${venue.value.yearEstablished}` : 'Est. Not set')
const previewRating = computed(() => '4.8')
const reviewCountLabel = computed(() => '186')

const previewGalleryTiles = computed(() => {
  const sortedGallery = [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
  const baseTiles = sortedGallery.slice(0, 5)
  const missing = Math.max(0, 5 - baseTiles.length)
  const filledTiles = [...baseTiles, ...previewPlaceholderImages.slice(0, missing)].map((item, index) => ({
    ...item,
    id: item.id || `preview-tile-${index}`,
    isPlaceholder: Boolean(item.isPlaceholder),
    overlayText: ''
  }))

  if (!filledTiles.length) {
    return previewPlaceholderImages.map((item, index) => ({
      ...item,
      id: item.id || `preview-tile-${index}`,
      overlayText: ''
    }))
  }

  const hiddenCount = Math.max(0, sortedGallery.length - 5)
  if (filledTiles[4]) {
    filledTiles[4] = {
      ...filledTiles[4],
      overlayText: hiddenCount ? `+${hiddenCount}` : ''
    }
  }

  return filledTiles
})

const sideImages = computed(() => previewGalleryTiles.value.slice(1, 5))

const lightboxGalleryImages = computed(() => {
  const source = gallery.value.length
    ? [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
    : previewPlaceholderImages

  return source.map((item, index) => ({
    id: item.id || `lightbox-${index}`,
    src: item.src,
    thumb: item.src,
    tagName: item.tagName || '',
    isCover: Boolean(item.isCover),
    isPlaceholder: Boolean(item.isPlaceholder)
  }))
})

const galleryPreviewCards = computed(() => lightboxGalleryImages.value.slice(0, 8))

const spaceTabs = computed(() => spaces.value.slice(0, 3).map((item, index) => ({
  id: item.id,
  name: item.name,
  icon: ['🏠', '🌿', '⛱️'][index] || '🏛️'
})))

const featuredSpaceBase = computed(() => (
  spaces.value.find((item) => item.id === selectedSpaceId.value) || spaces.value[0] || null
))

const featuredSpace = computed(() => {
  if (!featuredSpaceBase.value) return null
  const item = featuredSpaceBase.value
  const seatingCards = item.seatingSummary.length
    ? item.seatingSummary.slice(0, 4).map((entry, index) => {
      const [labelPart, valuePart] = String(entry).split(':')
      return {
        label: labelPart?.trim() || `Configuration ${index + 1}`,
        value: valuePart?.trim() || 'Flexible'
      }
    })
    : [
        { label: 'Banquet / Rounds', value: '600' },
        { label: 'Theatre', value: '800' },
        { label: 'Cocktail / Reception', value: '900' },
        { label: 'Conference', value: '300' }
      ]

  const amenities = item.amenities.length
    ? item.amenities.slice(0, 6)
    : ['AC / Climate Control', 'Stage', 'Dance Floor', 'Attached Washrooms', 'Dedicated Bar Area', 'WiFi']

  const equipment = [
    { label: 'PA / Sound System', included: true, value: 'Included' },
    { label: 'LED Wall', included: false, value: '₹25,000' },
    { label: 'Spotlights / Stage Lighting', included: true, value: 'Included' },
    { label: 'Live Streaming Setup', included: false, value: '₹15,000' },
    { label: 'Dance Floor (Portable)', included: true, value: 'Included' },
    { label: 'Mandap / Ceremony Area', included: true, value: 'Included' }
  ]

  return {
    ...item,
    primaryPrice: formatCurrency(item.minPrice) || 'Price on request',
    secondaryPrice: item.maxPrice ? `to ${formatCurrency(item.maxPrice)} per event` : 'Custom quote available',
    longDescription: item.description || `${item.name} is a flexible ${String(item.areaSqft || 'event').trim()} setup crafted for celebrations, social gatherings, and curated hosted experiences with comfortable guest flow and event-ready infrastructure.`,
    seatingCards,
    amenities,
    equipment
  }
})

const previewBadges = computed(() => {
  const badges = [
    { label: '✓ Verified', tone: 'bg-[#eef9f3] text-[#2d8b62]' },
    { label: '★ Featured', tone: 'bg-[#fff7df] text-[#b17f07]' }
  ]

  if (venue.value.type) {
    badges.push({ label: venue.value.type, tone: 'bg-[#f3ede7] text-[#6f5948]' })
  }

  venue.value.highlights.slice(0, 3).forEach((item) => {
    badges.push({ label: item, tone: 'bg-[#f3ede7] text-[#6f5948]' })
  })

  return badges.slice(0, 6)
})

const venueStory = computed(() => (
  venue.value.about
    || `Established to host memorable celebrations in ${venueCity.value}, ${venue.value.title || 'this venue'} brings together flexible spaces, hospitality support, and a layout designed for weddings, parties, and social events.`
))

const venueCallout = computed(() => {
  if (spaces.value.length) {
    const minGuests = policies.value.minGuests || '50'
    const maxGuests = policies.value.maxGuests || '1200'
    return `${venueCity.value}'s versatile venue setup combines ${spaces.value.length} event space${spaces.value.length === 1 ? '' : 's'} with room for gatherings from ${minGuests}-${maxGuests} guests.`
  }
  return `${venue.value.title || 'This venue'} is positioned as a flexible event destination with curated service support and an elegant guest-ready atmosphere.`
})

const venueHighlightsList = computed(() => {
  const items = venue.value.highlights.length
    ? venue.value.highlights
    : ['Award Winning', '5-Star Rated', 'Valet Parking', 'Wheelchair Accessible', 'Power Backup', 'CCTV Surveillance']
  return items.slice(0, 8)
})

const inHouseServicesList = computed(() => {
  const packageServices = packages.value.flatMap((item) => item.includedServices || [])
  const venueServices = Array.isArray(venue.value.inHouseServices) ? venue.value.inHouseServices : []
  const servicePool = [...venueServices, ...packageServices]
    .map((item) => String(item || '').trim())
    .filter(Boolean)

  const uniqueServices = [...new Set(servicePool)]
  return uniqueServices.length
    ? uniqueServices.slice(0, 8)
    : ['Catering', 'DJ / Music', 'Valet Parking', 'Event Coordination', 'Decoration', 'Photography', 'Bar', 'Guest Support']
})

const inHouseServiceColumns = computed(() => {
  const items = inHouseServicesList.value
  const midpoint = Math.ceil(items.length / 2)
  return [items.slice(0, midpoint), items.slice(midpoint)].filter((column) => column.length)
})

const partnerPreview = computed(() => partners.value.slice(0, 4))
const depositLabel = computed(() => policies.value.depositPercent ? `Deposit ${policies.value.depositPercent}%` : 'Deposit on request')

const heroFacts = computed(() => ([
  {
    label: 'Location',
    value: venue.value.address || venueCity.value
  },
  {
    label: 'Rating',
    value: `${previewRating.value} (${reviewCountLabel.value} reviews)`
  },
  {
    label: 'Capacity',
    value: policies.value.minGuests || policies.value.maxGuests
      ? `${policies.value.minGuests || 'Flexible'}-${policies.value.maxGuests || 'Flexible'} guests`
      : 'Guest range not added'
  },
  {
    label: 'Deposit',
    value: depositLabel.value
  }
]))

const placeholderInquiryPackages = [
  {
    id: 'placeholder-silver',
    name: 'Silver',
    description: 'Garden Lawn + Veg Standard menu + Basic decor',
    priceLabel: 'Rs. 5,00,000',
    metaLine: 'Up to 200 guests · 1 day',
    includedServices: ['Garden Lawn', 'Veg Menu', 'Basic Decor', 'Valet'],
    badge: ''
  },
  {
    id: 'placeholder-gold',
    name: 'Gold',
    description: 'Grand Ballroom + Veg Premium menu + Floral decor + DJ',
    priceLabel: 'Rs. 8,50,000',
    metaLine: 'Up to 400 guests · 1 day',
    includedServices: ['Grand Ballroom', 'Veg Premium Menu', 'Floral Décor', 'DJ', 'Valet', 'Photography'],
    badge: 'Popular'
  },
  {
    id: 'placeholder-platinum',
    name: 'Platinum',
    description: 'All spaces + Premium menu + Coordination + Photography + DJ',
    priceLabel: 'Rs. 15,00,000',
    metaLine: 'Up to 600 guests · 2 days',
    includedServices: ['All Spaces', 'Premium Menu', 'Coordination', 'Photography', 'DJ', 'Valet'],
    badge: ''
  }
]

const inquiryPackages = computed(() => {
  if (!packages.value.length) return placeholderInquiryPackages
  return packages.value.slice(0, 3).map((item, index) => ({
    id: item.id,
    name: item.name,
    description: item.description || 'Package details will appear here once configured.',
    priceLabel: item.priceLabel,
    metaLine: [
      item.maxGuests ? `Up to ${item.maxGuests} guests` : '',
      item.validHours ? `${item.validHours} hrs` : ''
    ].filter(Boolean).join(' · ') || 'Custom package',
    includedServices: item.includedServices.length ? item.includedServices : ['Decor', 'Catering', 'Guest Support'],
    badge: index === 1 ? 'Popular' : ''
  }))
})

const selectedPackage = computed(() => (
  inquiryPackages.value.find((item) => item.id === selectedPackageId.value) || inquiryPackages.value[0] || null
))

const placeholderMenus = [
  {
    id: 'veg-standard',
    name: 'Veg Standard',
    price: '₹850',
    minimumLabel: 'Minimum 100 plates',
    benchmarkType: 'Veg',
    dot: '●',
    activeTone: 'border-[#2f8a57] bg-[#f1fbf5] text-[#2f8a57]',
    groups: [
      { title: 'Veg starters', items: ['Paneer Tikka', 'Veg Seekh Kebab', 'Hara Bhara Kebab'] },
      { title: 'Veg main course', items: ['Dal Makhani', 'Shahi Paneer', 'Mix Veg Sabzi', 'Jeera Rice', 'Laccha Paratha'] },
      { title: 'Veg soup & salads', items: ['Tomato Basil Soup', 'Garden Fresh Salad'] },
      { title: 'Desserts', items: ['Gulab Jamun', 'Ice Cream', 'Kheer'] },
      { title: 'Live counters', items: ['Chaat Counter'] }
    ]
  },
  {
    id: 'veg-premium',
    name: 'Veg Premium',
    price: '₹1,050',
    minimumLabel: 'Minimum 150 plates',
    benchmarkType: 'Veg',
    dot: '●',
    activeTone: 'border-[#2f8a57] bg-[#f1fbf5] text-[#2f8a57]',
    groups: [
      { title: 'Veg starters', items: ['Paneer Malai Tikka', 'Dahi ke Kebab', 'Crispy Corn'] },
      { title: 'Veg main course', items: ['Paneer Lababdar', 'Navratan Korma', 'Dal Bukhara', 'Veg Biryani', 'Butter Naan'] },
      { title: 'Soups & salads', items: ['Sweet Corn Soup', 'Greek Salad', 'Sprout Chaat'] },
      { title: 'Desserts', items: ['Rasmalai', 'Brownie', 'Kulfi'] },
      { title: 'Live counters', items: ['Pasta Counter', 'Mojito Bar'] }
    ]
  },
  {
    id: 'nonveg-standard',
    name: 'Non-Veg Standard',
    price: '₹1,100',
    minimumLabel: 'Minimum 150 plates',
    benchmarkType: 'Non-Veg',
    dot: '●',
    activeTone: 'border-[#c14a3d] bg-[#fff3f1] text-[#c14a3d]',
    groups: [
      { title: 'Non-veg starters', items: ['Chicken Tikka', 'Mutton Seekh Kebab', 'Fish Finger'] },
      { title: 'Main course', items: ['Butter Chicken', 'Mutton Rogan Josh', 'Dal Makhani', 'Veg Pulao', 'Tandoori Roti'] },
      { title: 'Soups & salads', items: ['Hot & Sour Soup', 'Russian Salad'] },
      { title: 'Desserts', items: ['Moong Dal Halwa', 'Ice Cream'] },
      { title: 'Live counters', items: ['Shawarma Counter'] }
    ]
  }
]

const menuTabs = computed(() => placeholderMenus.map((item) => ({
  id: item.id,
  name: item.name,
  dot: item.dot,
  activeTone: item.activeTone
})))

const selectedMenu = computed(() => (
  placeholderMenus.find((item) => item.id === selectedMenuId.value) || placeholderMenus[0]
))

const menuBenchmarkText = computed(() => {
  const vegItems = placeholderMenus.filter((item) => item.benchmarkType === 'Veg').map((item) => item.price)
  const nonVegItem = placeholderMenus.find((item) => item.benchmarkType === 'Non-Veg')?.price || '₹1,100'
  return `Benchmark prices quoted at 250 pax — Veg from ${vegItems[0] || '₹850'}/plate, Non-Veg from ${nonVegItem}/plate`
})

const venueRuleItems = computed(() => ([
  {
    icon: '🍽️',
    label: 'Catering',
    value: [policies.value.inHouseCatering, policies.value.outsideCatering].filter(Boolean).join('. ') || 'Catering policy not added yet.'
  },
  {
    icon: '🌸',
    label: 'Decoration',
    value: policies.value.decorPolicy || 'Decoration policy not added yet.'
  },
  {
    icon: '🎵',
    label: 'DJ / Music',
    value: policies.value.musicPolicy || 'Music policy not added yet.'
  },
  {
    icon: '🍸',
    label: 'Alcohol',
    value: [policies.value.alcoholInhouse, policies.value.alcoholOutside].filter(Boolean).join('. ') || 'Alcohol policy not added yet.'
  },
  {
    icon: '🔊',
    label: 'Noise curfew',
    value: policies.value.noiseMusic ? `Music must end by ${policies.value.noiseMusic}.` : 'Noise or music curfew not added yet.'
  },
  {
    icon: '📋',
    label: 'House rules',
    value: policies.value.otherHouseRules || 'No additional house rules added yet.'
  }
]))

const bookingTermItems = computed(() => ([
  {
    icon: '👥',
    label: 'Guest count',
    value: policies.value.minGuests || policies.value.maxGuests
      ? `${policies.value.minGuests || 'Flexible'}-${policies.value.maxGuests || 'Flexible'} guests`
      : 'Guest count policy not added yet.'
  },
  {
    icon: '🔧',
    label: 'Setup time',
    value: policies.value.decorTime || 'Setup timing not added yet.'
  },
  {
    icon: '🧹',
    label: 'Cleanup time',
    value: policies.value.cleanTime || 'Cleanup timing not added yet.'
  },
  {
    icon: '💳',
    label: 'Deposit',
    value: policies.value.depositPercent ? `${policies.value.depositPercent}% of total to confirm booking` : 'Deposit terms not added yet.'
  },
  {
    icon: '↩️',
    label: 'Cancellation',
    value: policies.value.cancelTerms || 'Cancellation policy not added yet.'
  }
]))

const loadVenueGalleryPreview = async (venueId) => {
  const categoriesResponse = await api.getVenueGalleryCategories(new FormData())
  const categories = normalizeApiCollection(categoriesResponse?.data, ['categories', 'gallery_categories', 'items', 'list'])
  const tagIds = categories
    .map((item) => String(item?.id || item?.gallery_category_id || item?.tag_id || '').trim())
    .filter(Boolean)

  if (!tagIds.length) {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    const galleryResponse = await api.getVenueGallery(formData)
    return normalizeVenueGalleryEntries(normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list']))
  }

  const responses = await Promise.all(tagIds.map(async (tagId) => {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    formData.append('tag_id', tagId)
    const galleryResponse = await api.getVenueGallery(formData)
    return normalizeVenueGalleryEntries(normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list']))
  }))

  const seen = new Set()
  return responses.flat().filter((item) => {
    const key = `${item.id}-${item.src}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const loadVenue = async () => {
  loading.value = true
  error.value = ''

  try {
    const businessFormData = new FormData()
    businessFormData.append('business_id', route.params.id)
    const venueFormData = new FormData()
    venueFormData.append('business_id', route.params.id)
    venueFormData.append('venue_id', route.params.venueId)
    const [businessResult, venueResult] = await Promise.allSettled([
      api.editBusinessVendor(businessFormData),
      api.listVenuesBusinessVendor(venueFormData)
    ])

    let hasPartialFailure = false

    if (businessResult.status === 'fulfilled') {
      const businessPayload = Array.isArray(businessResult.value?.data?.data)
        ? businessResult.value.data.data[0]
        : businessResult.value?.data?.data || businessResult.value?.data || {}
      businessName.value = businessPayload?.business_name || businessPayload?.businessName || businessPayload?.name || businessName.value
    } else {
      hasPartialFailure = true
    }

    if (venueResult.status === 'fulfilled') {
      const venuePayload = venueResult.value?.data?.data || venueResult.value?.data || {}
      const venueItems = Array.isArray(venuePayload)
        ? venuePayload
        : venuePayload?.venues || venuePayload?.items || venuePayload?.list || venuePayload?.venue_list || []
      const matchedVenue = Array.isArray(venueItems)
        ? venueItems.find((item) => String(item?.id || item?.venue_id || item?.vendor_business_venue_id || '') === String(route.params.venueId))
        : null
      venue.value = normalizeVenue(matchedVenue || venueItems[0] || {})
    } else {
      hasPartialFailure = true
      venue.value = normalizeVenue({
        id: route.params.venueId,
        title: route.query.venueTitle || 'Venue detail',
        address: route.query.venueAddress || '',
        status: 'Draft'
      })
    }

    const spacesFormData = new FormData()
    spacesFormData.append('venue_id', route.params.venueId)
    const packagesFormData = new FormData()
    packagesFormData.append('venue_id', route.params.venueId)
    const partnersFormData = new FormData()
    partnersFormData.append('venue_id', route.params.venueId)
    const policiesFormData = new FormData()
    policiesFormData.append('venue_id', route.params.venueId)

    const [spacesResult, packagesResult, partnersResult, policiesResult, galleryResult] = await Promise.allSettled([
      api.listVenueSpaces(spacesFormData),
      api.getPackagesVenue(packagesFormData),
      api.listVenueVendorPartners(partnersFormData),
      api.editPoliciesVenue(policiesFormData),
      loadVenueGalleryPreview(String(route.params.venueId))
    ])

    if (spacesResult.status === 'fulfilled') {
      const spaceData = spacesResult.value?.data?.data || spacesResult.value?.data || {}
      const items = Array.isArray(spaceData) ? spaceData : spaceData?.spaces || spaceData?.items || spaceData?.list || []
      spaces.value = items.map((item, index) => normalizeSpace(item, index))
    } else {
      spaces.value = []
    }

    if (packagesResult.status === 'fulfilled') {
      const packageData = packagesResult.value?.data?.data || packagesResult.value?.data || {}
      const items = Array.isArray(packageData) ? packageData : packageData?.packages || packageData?.items || packageData?.list || []
      packages.value = items.map((item, index) => normalizePackage(item, index))
    } else {
      packages.value = []
    }

    if (partnersResult.status === 'fulfilled') {
      const partnerData = partnersResult.value?.data?.data || partnersResult.value?.data || {}
      const items = Array.isArray(partnerData) ? partnerData : partnerData?.partners || partnerData?.vendor_partners || partnerData?.items || partnerData?.list || []
      partners.value = items.map((item, index) => normalizePartner(item, index))
    } else {
      partners.value = []
      hasPartialFailure = true
    }

    if (policiesResult.status === 'fulfilled') {
      const policyItem = normalizeApiCollection(policiesResult.value?.data, ['policies', 'policy', 'items', 'list'])[0] || policiesResult.value?.data?.data || policiesResult.value?.data || {}
      policies.value = {
        inHouseCatering: policyItem?.in_house_catering || '',
        outsideCatering: policyItem?.outside_catering || '',
        decorPolicy: policyItem?.decor_policy || '',
        musicPolicy: policyItem?.music_policy || '',
        alcoholInhouse: policyItem?.alcohol_inhouse || '',
        alcoholOutside: policyItem?.alcohol_outside || '',
        minGuests: policyItem?.min_guests || '',
        maxGuests: policyItem?.max_guests || '',
        decorTime: policyItem?.decor_time || '',
        cleanTime: policyItem?.clean_time || '',
        noiseMusic: policyItem?.noise_music || '',
        otherHouseRules: policyItem?.other_house_rules || '',
        cancelTerms: policyItem?.cancel_terms || '',
        depositPercent: policyItem?.deposit_percent || ''
      }
    } else {
      hasPartialFailure = true
    }

    if (galleryResult.status === 'fulfilled') {
      gallery.value = galleryResult.value
    } else {
      gallery.value = []
      hasPartialFailure = true
    }

    if (hasPartialFailure) {
      error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
    }
  } catch (err) {
    error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
  } finally {
    loading.value = false
  }
}

const destroyGalleryLightbox = () => {
  if (galleryLightbox.value) {
    galleryLightbox.value.destroy(true)
    galleryLightbox.value = null
  }
}

const openVenueGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyGalleryLightbox()
  galleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.thumb,
      subHtml: image.tagName ? `<span class="text-sm font-semibold">${image.tagName}</span>` : ''
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false,
    mobileSettings: {
      controls: true,
      showCloseIcon: true,
      download: false
    }
  })
  galleryLightbox.value.openGallery(startIndex)
}

const openVenueGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openVenueGallery(startIndex >= 0 ? startIndex : 0)
}

const goBack = () => {
  router.push({
    name: 'business-detail',
    params: { id: route.params.id }
  })
}

onMounted(() => {
  loadVenue()
})

onBeforeUnmount(() => {
  destroyGalleryLightbox()
})
</script>
